import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';

/* ═══════════════════════════════════════════════════════════
   Documentation Page — Standalone (no console sidebar/header)
   Public-facing developer docs portal for Nieramind.AI
   ═══════════════════════════════════════════════════════════ */

const D = {
  primary: '#ea2a33',
  pL: 'rgba(234,42,51,.08)',
  bg: '#f8f6f6',
  w: '#ffffff',
  bdr: '#e5e7eb',
  t1: '#0f172a',
  t2: '#64748b',
  t3: '#94a3b8',
  dark: '#0f172a',
};

/* ── Sidebar Nav Data ── */
const SIDEBAR_NAV = [
  { group: 'Introduction', items: [
    { key: 'getting-started', icon: 'rocket_launch', label: 'Getting Started' },
    { key: 'core-concepts', icon: 'description', label: 'Core Concepts' },
  ]},
  { group: 'Development', items: [
    { key: 'api-reference', icon: 'terminal', label: 'API Reference' },
    { key: 'sdks', icon: 'package_2', label: 'SDKs & Libraries' },
    { key: 'webhooks', icon: 'webhook', label: 'Webhooks' },
  ]},
  { group: 'Customization', items: [
    { key: 'branding', icon: 'palette', label: 'Branding & UI' },
    { key: 'training', icon: 'history_edu', label: 'Training Models' },
  ]},
];

/* ── Quick Start Cards ── */
const QUICK_START = [
  { icon: 'smart_toy', title: 'Create your first Bot', desc: 'Follow our step-by-step guide to configure your first neural engine and deploy it.' },
  { icon: 'key', title: 'Authentication', desc: 'Learn how to securely authenticate your requests using Bearer tokens and API keys.' },
  { icon: 'settings_input_antenna', title: 'Configure Webhooks', desc: 'Enable real-time event processing to handle incoming messages and status updates.' },
  { icon: 'brush', title: 'Customize UI', desc: 'Use our React and Vue SDKs to theme the chat interface to match your brand identity.' },
];

/* ── Code Samples ── */
const CODE_SAMPLES = {
  cURL: `POST /v1/chat/completions
Host: api.nieramind.ai
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json

{
  "model": "niera-core-v2",
  "messages": [
    {
      "role": "system",
      "content": "You are a helpful assistant."
    },
    {
      "role": "user",
      "content": "Explain quantum computing in simple terms."
    }
  ],
  "stream": true
}`,
  'Node.js': `const response = await fetch(
  'https://api.nieramind.ai/v1/chat/completions',
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model: 'niera-core-v2',
      messages: [
        { role: 'system', content: 'You are a helpful assistant.' },
        { role: 'user', content: 'Explain quantum computing in simple terms.' }
      ],
      stream: true,
    }),
  }
);`,
  Python: `import requests

response = requests.post(
    'https://api.nieramind.ai/v1/chat/completions',
    headers={
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json',
    },
    json={
        'model': 'niera-core-v2',
        'messages': [
            {'role': 'system', 'content': 'You are a helpful assistant.'},
            {'role': 'user', 'content': 'Explain quantum computing in simple terms.'},
        ],
        'stream': True,
    }
)`,
};

/* ── TOC ── */
const TOC = ['Introduction', 'Quick Start', 'Code Samples', 'Authentication'];

/* ── useBreakpoint hook (inline) ── */
function useBreak() {
  const [w, setW] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { mob: w < 768, tab: w >= 768 && w < 1024, wide: w >= 1280 };
}

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
export default function DocumentationPage() {
  const navigate = useNavigate();
  const { mob, tab, wide } = useBreak();

  const [activeNav, setActiveNav] = useState('getting-started');
  const [codeLang, setCodeLang] = useState('cURL');
  const [codeCopied, setCodeCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeToc, setActiveToc] = useState('Introduction');

  /* ── Override App.css global overflow:hidden so this standalone page can scroll ── */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    // Save original values
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;
    const prevHtmlHeight = html.style.height;
    const prevBodyBg = body.style.background;
    // Override for natural page scrolling
    html.style.overflow = 'auto';
    html.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.height = 'auto';
    body.style.background = '#ffffff';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      html.style.height = prevHtmlHeight;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
      body.style.background = prevBodyBg;
    };
  }, []);

  const handleCopyCode = () => {
    navigator.clipboard?.writeText(CODE_SAMPLES[codeLang]).catch(() => {});
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const showLeftSidebar = !mob && !tab;
  const showRightSidebar = wide;

  return (
    <div style={{ minHeight: '100vh', background: D.w, fontFamily: "'Public Sans', 'Inter', sans-serif", color: D.t1 }}>

      {/* ═════ Top Navigation Bar ═════ */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50,
        borderBottom: `1px solid ${D.bdr}`,
        background: 'rgba(255,255,255,.85)', backdropFilter: 'blur(12px)',
      }}>
        <div style={{
          maxWidth: 1440, margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: 64, padding: mob ? '0 16px' : '0 40px',
        }}>
          {/* Left: Logo + Search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: mob ? 12 : 32 }}>
            {/* Hamburger (mobile/tablet) */}
            {(mob || tab) && (
              <button onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Menu" style={{
                border: 'none', background: 'none', cursor: 'pointer', padding: 4,
              }}>
                <Icon name={sidebarOpen ? 'close' : 'menu'} size={24} style={{ color: D.t1 }} />
              </button>
            )}
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
              onClick={() => navigate('/api-hub')}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: D.primary,
                display: 'grid', placeItems: 'center',
              }}>
                <Icon name="temp_preferences_custom" size={18} style={{ color: '#fff' }} />
              </div>
              <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.02em' }}>Nieramind.AI</span>
            </div>
            {/* Search */}
            {!mob && (
              <div style={{ position: 'relative' }}>
                <Icon name="search" size={18} style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: D.t3,
                }} />
                <input type="text" placeholder="Search documentation..."
                  style={{
                    width: tab ? 200 : 320, padding: '9px 48px 9px 38px', borderRadius: 10,
                    border: `1px solid ${D.bdr}`, background: '#f8fafc', fontSize: 13,
                    outline: 'none', color: D.t1, boxSizing: 'border-box',
                  }} />
                <kbd style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 10, fontWeight: 500, color: D.t3, background: D.w,
                  border: `1px solid ${D.bdr}`, borderRadius: 4, padding: '1px 6px',
                }}>⌘K</kbd>
              </div>
            )}
          </div>

          {/* Right: Nav links + buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: mob ? 10 : 24 }}>
            {!mob && !tab && (
              <nav style={{ display: 'flex', gap: 24 }}>
                {['Showcase', 'Pricing', 'Changelog'].map(l => (
                  <a key={l} href="#" style={{
                    fontSize: 13, fontWeight: 500, color: D.t2, textDecoration: 'none',
                    transition: 'color .15s',
                  }}
                    onMouseEnter={e => e.target.style.color = D.primary}
                    onMouseLeave={e => e.target.style.color = D.t2}
                  >{l}</a>
                ))}
              </nav>
            )}
            {!mob && <div style={{ width: 1, height: 24, background: D.bdr }} />}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => navigate('/api-hub')} style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: D.primary, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>Console</button>
              {!mob && (
                <button style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: '#f1f5f9', color: D.t1, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}>Support</button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ═════ Body: 3-Column Layout ═════ */}
      <div style={{
        maxWidth: 1440, margin: '0 auto',
        display: 'flex', padding: mob ? '0' : '0 40px',
      }}>

        {/* ── Left Sidebar ── */}
        {showLeftSidebar && (
          <aside style={{
            position: 'sticky', top: 64, width: 240, flexShrink: 0,
            height: 'calc(100vh - 64px)', overflowY: 'auto',
            borderRight: `1px solid ${D.bdr}08`, paddingTop: 28, paddingRight: 24,
          }}>
            <SidebarNav activeNav={activeNav} setActiveNav={setActiveNav} />
          </aside>
        )}

        {/* Mobile sidebar overlay */}
        {(mob || tab) && sidebarOpen && (
          <div onClick={() => setSidebarOpen(false)} style={{
            position: 'fixed', inset: 0, top: 64, background: 'rgba(0,0,0,.2)',
            zIndex: 40,
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              width: 280, height: '100%', background: D.w,
              borderRight: `1px solid ${D.bdr}`, padding: '24px 20px',
              overflowY: 'auto', boxShadow: '4px 0 20px rgba(0,0,0,.08)',
            }}>
              <SidebarNav activeNav={activeNav} setActiveNav={(k) => { setActiveNav(k); setSidebarOpen(false); }} />
            </div>
          </div>
        )}

        {/* ── Main Content ── */}
        <main style={{
          flex: 1, minWidth: 0,
          padding: mob ? '28px 16px' : tab ? '36px 24px' : '36px 48px',
          maxWidth: 880,
        }}>
          {/* Breadcrumb */}
          <nav style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
            <a href="#" style={{ fontSize: 13, fontWeight: 500, color: D.t3, textDecoration: 'none' }}
              onMouseEnter={e => e.target.style.color = D.primary}
              onMouseLeave={e => e.target.style.color = D.t3}
            >Docs</a>
            <Icon name="chevron_right" size={14} style={{ color: D.t3 }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: D.t1 }}>Getting Started</span>
          </nav>

          {/* Hero */}
          <h1 style={{
            fontSize: mob ? 32 : 42, fontWeight: 900, letterSpacing: '-.03em',
            lineHeight: 1.1, marginBottom: 14,
          }}>Documentation</h1>
          <p style={{
            fontSize: mob ? 15 : 17, lineHeight: 1.7, color: D.t2, marginBottom: 40,
            maxWidth: 620,
          }}>
            Build the future of communication with Nieramind.AI.
            Integrate powerful AI chatbots into your workflow in minutes.
            Our flexible APIs and pre-built components allow you to scale
            intelligent conversations globally.
          </p>

          {/* ═════ Quick Start ═════ */}
          <section style={{ marginBottom: 56 }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', marginBottom: 24 }}>Quick Start</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: mob ? '1fr' : 'repeat(2, 1fr)',
              gap: 16,
            }}>
              {QUICK_START.map((card, i) => (
                <a key={i} href="#" style={{
                  display: 'flex', flexDirection: 'column', padding: 22,
                  borderRadius: 12, border: `1px solid ${D.bdr}`, background: D.w,
                  textDecoration: 'none', color: 'inherit',
                  boxShadow: '0 1px 3px rgba(0,0,0,.04)',
                  transition: 'all .2s',
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.borderColor = `${D.primary}50`;
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,.08)';
                    e.currentTarget.querySelector('.qs-icon').style.background = D.primary;
                    e.currentTarget.querySelector('.qs-icon').style.color = '#fff';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = D.bdr;
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.04)';
                    e.currentTarget.querySelector('.qs-icon').style.background = D.pL;
                    e.currentTarget.querySelector('.qs-icon').style.color = D.primary;
                  }}
                >
                  <div className="qs-icon" style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: D.pL, color: D.primary,
                    display: 'grid', placeItems: 'center', marginBottom: 14,
                    transition: 'all .2s',
                  }}>
                    <Icon name={card.icon} size={20} />
                  </div>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>{card.title}</h3>
                  <p style={{ fontSize: 13, lineHeight: 1.55, color: D.t2 }}>{card.desc}</p>
                </a>
              ))}
            </div>
          </section>

          {/* ═════ Sample API Request ═════ */}
          <section style={{ marginBottom: 56 }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 18, flexWrap: 'wrap', gap: 12,
            }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em' }}>Sample API Request</h2>
              <div style={{ display: 'flex', gap: 6 }}>
                {Object.keys(CODE_SAMPLES).map(lang => (
                  <button key={lang} onClick={() => { setCodeLang(lang); setCodeCopied(false); }} style={{
                    padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 700,
                    background: codeLang === lang ? '#f1f5f9' : 'transparent',
                    color: codeLang === lang ? D.t1 : D.t3,
                    transition: 'all .15s',
                  }}>{lang}</button>
                ))}
              </div>
            </div>

            {/* Code block */}
            <div style={{
              borderRadius: 12, overflow: 'hidden', background: D.dark,
              boxShadow: '0 16px 48px rgba(0,0,0,.16)',
            }}>
              {/* Window chrome */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,.08)',
                background: 'rgba(30,41,59,.6)',
              }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: '#334155' }} />
                  ))}
                </div>
                <button onClick={handleCopyCode} style={{
                  border: 'none', background: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: 12, fontWeight: 500,
                  color: codeCopied ? '#34d399' : D.t3, transition: 'color .15s',
                }}>
                  <Icon name={codeCopied ? 'check' : 'content_copy'} size={14} />
                  {codeCopied ? 'Copied!' : 'Copy Code'}
                </button>
              </div>
              {/* Code body */}
              <div style={{ padding: mob ? 16 : 24, overflowX: 'auto' }}>
                <pre style={{
                  fontSize: mob ? 12 : 13, lineHeight: 1.7, margin: 0,
                  fontFamily: "'Courier New', monospace", color: '#cbd5e1',
                  whiteSpace: 'pre',
                }}>
                  {CODE_SAMPLES[codeLang]}
                </pre>
              </div>
            </div>
          </section>

          {/* ═════ Footer ═════ */}
          <footer style={{
            marginTop: 64, borderTop: `1px solid ${D.bdr}`, paddingTop: 36, paddingBottom: 36,
            display: 'flex', flexDirection: mob ? 'column' : 'row',
            alignItems: mob ? 'center' : 'center', justifyContent: 'space-between',
            gap: 20,
          }}>
            <p style={{ fontSize: 13, color: D.t3 }}>© 2024 Nieramind.AI. All rights reserved.</p>
            <div style={{ display: 'flex', gap: 32 }}>
              {['Privacy', 'Terms', 'GitHub'].map(l => (
                <a key={l} href="#" style={{ fontSize: 13, color: D.t3, textDecoration: 'none' }}
                  onMouseEnter={e => e.target.style.color = D.primary}
                  onMouseLeave={e => e.target.style.color = D.t3}
                >{l}</a>
              ))}
            </div>
          </footer>
        </main>

        {/* ── Right Sidebar (TOC + Help) ── */}
        {showRightSidebar && (
          <aside style={{
            position: 'sticky', top: 64, width: 240, flexShrink: 0,
            height: 'calc(100vh - 64px)', overflowY: 'auto',
            paddingTop: 36, paddingLeft: 32,
          }}>
            {/* On This Page */}
            <div style={{ marginBottom: 32 }}>
              <h5 style={{
                fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
                letterSpacing: '.08em', color: D.t1, marginBottom: 16,
              }}>On This Page</h5>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, borderLeft: `1px solid ${D.bdr}` }}>
                {TOC.map(item => {
                  const active = activeToc === item;
                  return (
                    <li key={item}>
                      <a href="#" onClick={e => { e.preventDefault(); setActiveToc(item); }}
                        style={{
                          display: 'block', padding: '6px 0 6px 16px', fontSize: 13,
                          fontWeight: active ? 600 : 400,
                          color: active ? D.primary : D.t2,
                          textDecoration: 'none',
                          borderLeft: `2px solid ${active ? D.primary : 'transparent'}`,
                          marginLeft: -1,
                          transition: 'all .15s',
                        }}
                      >{item}</a>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Need help? card */}
            <div style={{
              background: '#f8fafc', borderRadius: 14, padding: 22,
              border: `1px solid ${D.bdr}`, marginBottom: 18,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10, background: D.w,
                display: 'grid', placeItems: 'center', marginBottom: 12,
                boxShadow: '0 1px 3px rgba(0,0,0,.06)',
              }}>
                <Icon name="support_agent" size={20} style={{ color: D.primary }} />
              </div>
              <h6 style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Need help?</h6>
              <p style={{ fontSize: 11, lineHeight: 1.6, color: D.t2, marginBottom: 14 }}>
                Can't find what you're looking for? Our engineering team is here to assist you.
              </p>
              <button style={{
                width: '100%', padding: '9px 0', borderRadius: 8,
                border: `1px solid ${D.bdr}`, background: D.w,
                fontSize: 12, fontWeight: 700, color: D.t1, cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,.04)',
              }}>Contact Support</button>
            </div>

            {/* Community card */}
            <div style={{
              background: D.pL, borderRadius: 14, padding: 22,
              border: `1px solid ${D.primary}10`,
            }}>
              <h6 style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Community</h6>
              <p style={{ fontSize: 11, lineHeight: 1.6, color: D.t2, marginBottom: 12 }}>
                Join over 10,000 developers in our community discord.
              </p>
              <a href="#" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                fontSize: 12, fontWeight: 700, color: D.primary, textDecoration: 'none',
              }}>
                Join Discord <Icon name="open_in_new" size={14} />
              </a>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Sidebar Navigation (reused in desktop + mobile overlay)
   ═══════════════════════════════════════════════════════════ */
function SidebarNav({ activeNav, setActiveNav }) {
  return (
    <nav style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {SIDEBAR_NAV.map(section => (
        <div key={section.group}>
          <h5 style={{
            fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: '.08em', color: D.t3, marginBottom: 12,
          }}>{section.group}</h5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {section.items.map(item => {
              const active = activeNav === item.key;
              return (
                <button key={item.key} onClick={() => setActiveNav(item.key)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                  borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left',
                  background: active ? D.pL : 'transparent',
                  fontSize: 13, fontWeight: active ? 700 : 500,
                  color: active ? D.primary : D.t2,
                  transition: 'all .15s', width: '100%',
                }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = D.t1; }}}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = D.t2; }}}
                >
                  <Icon name={item.icon} size={18} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}
